import { Box, Divider, Group, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconLanguage } from '@tabler/icons-react';
import {
  usePreferencesStore,
  SUPPORTED_LANGUAGES,
  TIMEZONES,
} from '../../../stores';
import Select from '../../../components/ui/inputs/Select';
import SegmentedControl from '../../../components/ui/navigation/SegmentedControl';

export default function LanguageRegionSection() {
  const language = usePreferencesStore((state) => state.language);
  const setLanguage = usePreferencesStore((state) => state.setLanguage);
  const timezone = usePreferencesStore((state) => state.timezone);
  const setTimezone = usePreferencesStore((state) => state.setTimezone);
  const dateFormat = usePreferencesStore((state) => state.dateFormat);
  const setDateFormat = usePreferencesStore((state) => state.setDateFormat);
  const timeFormat = usePreferencesStore((state) => state.timeFormat);
  const setTimeFormat = usePreferencesStore((state) => state.setTimeFormat);
  const daylightSaving = usePreferencesStore((state) => state.daylightSaving);
  const setDaylightSaving = usePreferencesStore((state) => state.setDaylightSaving);

  const languageOptions = SUPPORTED_LANGUAGES.map((lang) => ({
    value: lang.code,
    label: `${lang.flag} ${lang.label}`,
  }));

  const timezoneOptions = TIMEZONES.map((tz) => ({
    value: tz.value,
    label: tz.label,
  }));

  const dateFormatOptions = [
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (US)' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (EU)' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (ISO)' },
  ];

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group gap="sm">
        <ThemeIcon size="lg" radius="md" variant="light" color="blue">
          <IconLanguage size={20} />
        </ThemeIcon>
        <Box>
          <Text fw={600}>Language & Region</Text>
          <Text size="xs" c="dimmed">
            Set your language and regional preferences
          </Text>
        </Box>
      </Group>

      <Divider />

      <Select
        label="Language"
        placeholder="Select language"
        data={languageOptions}
        value={language}
        onChange={(value) => value && setLanguage(value)}
        searchable
      />

      <Select
        label="Timezone"
        placeholder="Select timezone"
        data={timezoneOptions}
        value={timezone}
        onChange={(value) => value && setTimezone(value)}
        searchable
      />

      <Box>
        <Text size="sm" fw={500} mb="xs">
          Daylight Saving Time
        </Text>
        <Text size="xs" c="dimmed" mb="xs">
          Adjust clock automatically for daylight saving time
        </Text>
        <SegmentedControl
          fullWidth
          activeGradient
          data={[
            { label: 'Auto', value: 'auto' },
            { label: 'On', value: 'on' },
            { label: 'Off', value: 'off' },
          ]}
          value={daylightSaving}
          onChange={(value) => setDaylightSaving(value as 'auto' | 'on' | 'off')}
        />
        <Text size="xs" c="dimmed" mt="xs">
          {daylightSaving === 'auto' && 'Automatically adjusts based on your timezone rules'}
          {daylightSaving === 'on' && 'Always apply daylight saving time offset'}
          {daylightSaving === 'off' && 'Never apply daylight saving time offset'}
        </Text>
      </Box>

      <Divider />

      <Select
        label="Date Format"
        data={dateFormatOptions}
        value={dateFormat}
        onChange={(value) => value && setDateFormat(value)}
      />

      <Box>
        <Text size="sm" fw={500} mb="xs">
          Time Format
        </Text>
        <SegmentedControl
          fullWidth
          activeGradient
          data={[
            { label: '12-hour', value: '12h' },
            { label: '24-hour', value: '24h' },
          ]}
          value={timeFormat}
          onChange={(value) => setTimeFormat(value as '12h' | '24h')}
        />
      </Box>
    </Stack>
  );
}
